import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import PatternBackground from "@/components/PatternBackground";
import OverlayCard from "@/components/OverlayCard";
import lodgeExterior from "@/assets/lodge-exterior.jpg";
import diningHero from "@/assets/4e0d1a74-3b6a-41d7-8e01-5248aaa11250.jpg";
import roomFamily from "@/assets/bea.jpg";
import safariLandscape from "@/assets/safari-landscape.jpg";
const About = () => {
  return <PatternBackground pattern="palm" className="min-h-screen">
      <div className="py-20">
        {/* Header */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-16">
          <OverlayCard size="lg" className="text-center mx-auto">
            <h1 className="font-heading text-4xl md:text-6xl font-bold text-foreground mb-6">About Kijani Arusha Airport Lodge</h1>
            <p className="text-xl text-muted-foreground">
              A peaceful sanctuary near Arusha Airport, where every journey begins and ends with care.
            </p>
          </OverlayCard>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-20">
          {/* Our Story Section with Creative Image Overlap */}
          <section>
            <OverlayCard size="lg" className="mx-auto mb-16">
              <h2 className="font-heading text-3xl md:text-4xl font-bold mb-8 text-center">Our Story</h2>
              <p className="text-lg text-muted-foreground text-center mb-12">
                Discover the passion and vision behind Kijani Arusha Airport Lodge.
              </p>
            </OverlayCard>
            
            {/* Creative Overlapping Design */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
              {/* First Story Block */}
              <div className="relative">
                <div className="aspect-[4/3] rounded-2xl overflow-hidden shadow-elegant">
                  <img src={lodgeExterior} alt="Kijani Arusha Airport Lodge exterior view" className="w-full h-full object-cover" />
                </div>
                <div className="static mt-8 lg:absolute lg:-bottom-8 lg:-right-8 bg-white/75 backdrop-blur-sm rounded-2xl shadow-elegant p-8 max-w-md border border-white/20">
                  <h3 className="font-heading text-2xl font-bold text-foreground mb-4">Where It All Began</h3>
                  <p className="text-muted-foreground leading-relaxed">
                    Founded with a dream to create an authentic Tanzanian experience, Kijani Arusha Airport Lodge was born from a passion for hospitality and love for Tanzania's natural beauty. Our journey began with a simple vision: to provide a home away from home for travelers seeking genuine connection with Africa.
                  </p>
                </div>
              </div>

              {/* Second Story Block - Opposite Layout */}
              <div className="relative lg:order-first">
                <div className="aspect-[4/3] rounded-2xl overflow-hidden shadow-elegant">
                  <img src={diningHero} alt="Lodge dining area and local cuisine" className="w-full h-full object-cover" />
                </div>
                <div className="static mt-8 lg:absolute lg:-bottom-8 lg:-left-8 bg-white/75 backdrop-blur-sm rounded-2xl shadow-elegant p-8 max-w-md border border-white/20">
                  <h3 className="font-heading text-2xl font-bold text-foreground mb-4">Rooted in Tradition</h3>
                  <p className="text-muted-foreground leading-relaxed">
                    Every dish we serve, every room we design, and every experience we craft is deeply rooted in Tanzanian culture. We believe in preserving traditions while offering modern comfort, creating a bridge between authentic local life and world-class hospitality.
                  </p>
                </div>
              </div>
            </div>

            {/* Third Story Block - Centered */}
            <div className="mt-20">
              <div className="relative max-w-4xl mx-auto">
                <div className="aspect-[16/9] rounded-2xl overflow-hidden shadow-elegant">
                  <img src={safariLandscape} alt="Tanzania safari landscape with acacia trees" className="w-full h-full object-cover" />
                </div>
              <div className="static mt-8 lg:absolute lg:-top-8 lg:-right-8 bg-white/75 backdrop-blur-sm rounded-2xl shadow-elegant p-8 max-w-md border border-white/20">
                <h3 className="font-heading text-2xl font-bold text-foreground mb-4">Our Commitment to Tanzania</h3>
                <p className="text-muted-foreground leading-relaxed">
                  Located just minutes from Arusha Airport, we serve as the perfect gateway to Tanzania's incredible wildlife and landscapes. Our commitment extends beyond hospitality – we're dedicated to sustainable tourism, supporting local communities, and preserving the natural environment that makes Tanzania so extraordinary. Every stay with us contributes to conservation efforts and community development initiatives.
                </p>
              </div>
              </div>
            </div>

            {/* Fourth Story Block - Side by side */}
            <div className="mt-20 md:mt-32 grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
              <div className="relative">
                <div className="aspect-[4/3] rounded-2xl overflow-hidden shadow-elegant">
                  <img src={roomFamily} alt="Comfortable family room interior" className="w-full h-full object-cover" />
                </div>
                <div className="static mt-8 lg:absolute lg:-top-8 lg:-right-8 bg-white/75 backdrop-blur-sm rounded-2xl shadow-elegant p-8 max-w-md border border-white/20">
                  <h3 className="font-heading text-2xl font-bold text-foreground mb-4">Your Home in Africa</h3>
                  <p className="text-muted-foreground leading-relaxed">
                    Our 9 beautifully appointed en-suite rooms are designed as tranquil retreats where comfort meets authenticity. Each space tells a story of Tanzanian craftsmanship, featuring locally sourced materials and traditional design elements that connect you to the land and its people.
                  </p>
                </div>
              </div>

              {/* Future Vision */}
              <div className="lg:pl-8">
                <Card className="bg-gradient-to-br from-primary/5 to-primary/10 border-primary/20 shadow-elegant">
                  <CardContent className="p-8">
                    <h3 className="font-heading text-2xl font-bold text-foreground mb-6">Looking Forward</h3>
                    <p className="text-muted-foreground leading-relaxed mb-6">
                      As we continue our journey, we remain committed to evolving while staying true to our core values. We're constantly enhancing our offerings, expanding our conservation partnerships, and finding new ways to showcase the beauty and richness of Tanzanian culture.
                    </p>
                    <p className="text-primary font-medium">
                      "Every guest who walks through our doors becomes part of our story, and we become part of theirs."
                    </p>
                  </CardContent>
                </Card>
              </div>
            </div>
          </section>

          {/* Call to Action */}
          <section>
            <OverlayCard size="lg" className="mx-auto text-center">
              <h2 className="font-heading text-3xl md:text-4xl font-bold mb-6">Ready to Experience Kijani?</h2>
              <p className="text-lg text-muted-foreground mb-8 max-w-2xl mx-auto">
                Join us for an authentic Tanzanian experience where every detail is crafted with care, 
                and every moment is an opportunity to connect with the beauty of Africa.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button size="lg" asChild className="bg-primary hover:bg-primary/90">
                  <Link to="/book">Book Your Stay</Link>
                </Button>
                <Button size="lg" variant="outline" asChild>
                  <Link to="/contact">Get In Touch</Link>
                </Button>
              </div>
            </OverlayCard>
          </section>
        </div>
      </div>
    </PatternBackground>;
};
export default About;